import type * as Monaco from "monaco-editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { lintSource, useDeleteFile, useFlights, useFrame, useProjectFile, useProjectFiles, useReports, useSaveFile, useSubmitRun } from "../../api/hooks";
import type { Problem } from "../../api/types";
import { useProblems } from "../../app/problems";
import { usePendingOpenFile, usePublishActiveFile } from "../../app/activeFile";
import { useSession } from "../../app/store";
import { DeliverToggle } from "../../components/DeliverToggle";
import { Split } from "../../components/Split";
import { Empty, Modal, errText } from "../../components/ui";
import type { TriggerGraph } from "../../graph/types";
import { TriggerWorkbench } from "../../trigger/TriggerWorkbench";
import "./triggers.css";

const NEW_TRIGGER = (name: string) => `from reports_studio.triggers import trigger, rising_edge


@trigger(name="${name}", reports=[])
def ${name}(ctx):
    """Describe when this trigger fires and what it captures."""
    alt = ctx.p("ALT")
    if rising_edge(alt < 10000):
        ctx.snapshot("${name.toUpperCase()}", window=ctx.window(start=ctx.t, length=10), alt=float(alt))
        ctx.disarm()
`;

export function TriggersPage() {
  const { projectId, openTriggerFile, setOpenTriggerFile, activeFlight, selectedFlights, setCurrentRun, setDock, toast, theme, devDeliver } = useSession();
  const files = useProjectFiles(projectId, "triggers");
  const selected = openTriggerFile;
  const file = useProjectFile(projectId, selected);
  const save = useSaveFile(projectId);
  const del = useDeleteFile(projectId);
  const submit = useSubmitRun();
  const flights = useFlights();
  const frame = useFrame(projectId);
  const reports = useReports(projectId);
  const navigate = useNavigate();
  const [draft, setDraft] = useState<string | null>(null);
  const [graph, setGraph] = useState<TriggerGraph | null>(null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [creating, setCreating] = useState(false);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const setDockProblems = useProblems((s) => s.setProblems);
  const setJump = useProblems((s) => s.setJump);

  useEffect(() => {
    if (!files.data || files.isFetching) return;
    if (selected && files.data.some((f) => f.path === selected)) return;
    setOpenTriggerFile(files.data[0]?.path ?? null);
  }, [files.data, files.isFetching, selected, setOpenTriggerFile]);
  useEffect(() => setDraft(null), [selected, file.data?.modified]);
  usePublishActiveFile(selected);
  usePendingOpenFile("triggers/", setOpenTriggerFile);

  const source = draft ?? file.data?.content ?? "";
  const dirty = draft !== null && draft !== (file.data?.content ?? "");

  // lint on debounce
  useEffect(() => {
    if (!projectId || !selected || !file.data) return;
    const h = setTimeout(() => {
      lintSource(projectId, selected, source)
        .then((r) => {
          setProblems(r.problems);
          setDockProblems(selected, r.problems);
        })
        .catch(() => undefined);
    }, 500);
    return () => clearTimeout(h);
  }, [projectId, selected, source, file.data, setDockProblems]);

  // Problems dock -> jump to line
  useEffect(() => {
    setJump((f, line, col) => {
      if (f !== selected) setOpenTriggerFile(f);
      setTimeout(() => {
        const ed = editorRef.current;
        const monaco = monacoRef.current;
        if (!ed || !monaco) return;
        ed.revealLineInCenter(line);
        ed.setPosition({ lineNumber: line, column: col + 1 });
        ed.focus();
      }, f !== selected ? 300 : 0);
    });
    return () => setJump(null);
  }, [selected, setJump, setOpenTriggerFile]);

  const doSave = useCallback(() => {
    if (!selected || draft === null) return;
    save.mutate(
      { path: selected, content: draft },
      {
        onSuccess: (r) => {
          toast("Saved", "success");
          if (r.problems) {
            setProblems(r.problems);
            setDockProblems(selected, r.problems);
          }
        },
        onError: (e) => toast(errText(e), "error"),
      },
    );
  }, [selected, draft, save, toast, setDockProblems]);

  // never deliver reports produced by unsaved editor text
  const deliver = devDeliver && !dirty;
  const runOn = (flightIds: string[]) => {
    if (!projectId || !selected || !flightIds.length) return;
    submit.mutate(
      {
        project: projectId,
        flights: flightIds,
        trace: "all",
        deliver,
        source_override: dirty ? { path: selected, source } : undefined,
        label: `trigger ${selected.split("/").pop()}${dirty ? " (unsaved)" : ""}`,
      },
      {
        onSuccess: (r) => {
          setCurrentRun(r.id);
          setDock(true, "runlog");
          toast(`Run ${r.id} started${deliver ? " · delivering via subscriptions" : ""}`, "success");
          navigate("/analysis");
        },
        onError: (e) => toast(errText(e), "error"),
      },
    );
  };

  const flightName = (id: string | null) => {
    const f = flights.data?.find((x) => x.id === id);
    return f ? `${f.tail ?? "?"} ${f.date?.slice(0, 10) ?? ""}` : "no flight selected";
  };

  const frameMnemonics = useMemo(() => new Set(frame.data?.parameters.map((p) => p.mnemonic) ?? []), [frame.data]);
  const reportNames = useMemo(() => new Set((reports.data ?? []).map((r) => r.report?.name).filter(Boolean) as string[]), [reports.data]);

  if (!projectId) return <Empty title="No project selected" />;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Triggers</h1>
        <span className="sub">Python against the runtime API; the graph is derived from the code</span>
        <span className="spacer" />
        <button className="btn" onClick={() => setCreating(true)}>
          ＋ New trigger
        </button>
        <button className="btn primary" disabled={!dirty || save.isPending} onClick={doSave} title="Ctrl+S">
          Save
        </button>
      </div>
      <div className="page-body">
        <Split id="triggers" sizes={[200, "1fr", 300]} minSizes={[140, 400, 200]}>
          <div className="pane">
            <div className="pane-head">Files</div>
            <div className="list">
              {files.data?.map((f) => (
                <div key={f.path} className={`list-item${f.path === selected ? " sel" : ""}`} onClick={() => setOpenTriggerFile(f.path)}>
                  <span className="name mono">{f.name}</span>
                </div>
              ))}
              {files.data && !files.data.length ? <div className="empty small">No trigger files.</div> : null}
            </div>
          </div>
          <div className="pane" style={{ overflow: "hidden" }}>
            {selected && file.data ? (
              <TriggerWorkbench
                key={selected}
                filename={selected}
                source={source}
                onChange={setDraft}
                onGraph={setGraph}
                onSave={doSave}
                problems={problems}
                theme={theme}
                onEditorMount={(ed, monaco) => {
                  editorRef.current = ed;
                  monacoRef.current = monaco;
                }}
                toolbar={
                  <>
                    {dirty ? <span className="badge warn">modified</span> : null}
                    <button
                      className="btn sm primary"
                      disabled={!activeFlight || submit.isPending}
                      onClick={() => activeFlight && runOn([activeFlight])}
                      title={dirty ? "Runs the unsaved editor text" : "Run the project on the active flight"}
                    >
                      ▶ Run on {flightName(activeFlight)}
                    </button>
                    {selectedFlights.length > 1 ? (
                      <button className="btn sm" disabled={submit.isPending} onClick={() => runOn(selectedFlights)}>
                        ▶ Run on {selectedFlights.length} selected
                      </button>
                    ) : null}
                    <DeliverToggle disabled={dirty} disabledReason="Save the trigger first: reports from unsaved editor text are never delivered" />
                  </>
                }
              />
            ) : (
              <Empty title="Select a trigger file">
                Triggers are plain Python: <code>@trigger</code> functions or <code>Trigger</code> subclasses with <code>setup</code>,{" "}
                <code>tick</code> and <code>finish</code>. Helpers such as <code>all_of</code>, <code>held_for</code>, <code>stable</code>{" "}
                and <code>rising_edge</code> show up as graph nodes with traced lanes.
              </Empty>
            )}
          </div>
          <div className="pane">
            <div className="pane-head">Trigger</div>
            <div className="pane-body col">
              {graph?.triggers.length ? (
                graph.triggers.map((t) => (
                  <div key={t.name} className="card">
                    <div className="row">
                      <strong className="mono">{t.name}</strong>
                      <span className="badge">{t.kind}</span>
                      {t.end_of_flight ? <span className="badge info" title="snapshots only in finish()">end-of-flight</span> : null}
                    </div>
                    {t.description ? (
                      <div className="small" style={{ marginTop: 4, opacity: 0.85 }}>
                        {t.description}
                      </div>
                    ) : (
                      <div className="faint small" style={{ marginTop: 4 }}>
                        Add a docstring under the trigger to describe it here.
                      </div>
                    )}
                    <div className="kv" style={{ marginTop: 6 }}>
                      <span>reports</span>
                      <span>
                        {t.reports.length
                          ? t.reports.map((r) => (
                              <span key={r} className={`badge ${reportNames.has(r) ? "ok" : "err"}`} title={reportNames.has(r) ? "" : "no such report"}>
                                {r}
                              </span>
                            ))
                          : <span className="faint">none (reports bind by trigger name)</span>}
                      </span>
                      {t.depends_on.length ? (
                        <>
                          <span>depends on</span>
                          <span>{t.depends_on.join(", ")}</span>
                        </>
                      ) : null}
                      <span>phases</span>
                      <span>{Object.keys(t.phases).join(", ") || "—"}</span>
                    </div>
                    <h4 style={{ marginTop: 8 }}>Parameters {t.dynamic_parameters ? <span className="badge">+ dynamic</span> : null}</h4>
                    <div className="row wrap">
                      {t.parameters.length ? (
                        t.parameters.map((m) => (
                          <span
                            key={m}
                            className={`badge ${frame.data ? (frameMnemonics.has(m) ? "ok" : "err") : ""}`}
                            title={frame.data ? (frameMnemonics.has(m) ? "in frame" : "missing from the frame definition") : ""}
                          >
                            {m}
                          </span>
                        ))
                      ) : (
                        <span className="faint small">
                          {t.dynamic_parameters ? "mnemonics come from inputs at runtime" : "no ctx.p() calls found"}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <span className="dim small">No trigger found in this file yet.</span>
              )}
              {graph?.warnings.length ? (
                <div className="card">
                  <h4>Graph notes</h4>
                  {graph.warnings.map((w, i) => (
                    <div key={i} className="small dim">
                      {w}
                    </div>
                  ))}
                </div>
              ) : null}
              {selected ? (
                <button
                  className="btn sm danger"
                  onClick={() => {
                    if (confirm(`Delete ${selected}?`))
                      del.mutate(selected, { onSuccess: () => setOpenTriggerFile(null), onError: (e) => toast(errText(e), "error") });
                  }}
                >
                  Delete file
                </button>
              ) : null}
            </div>
          </div>
        </Split>
      </div>
      {creating ? (
        <NewTriggerDialog
          onClose={() => setCreating(false)}
          onCreate={(name) =>
            save.mutate(
              { path: `triggers/${name}.py`, content: NEW_TRIGGER(name) },
              {
                onSuccess: () => {
                  setOpenTriggerFile(`triggers/${name}.py`);
                  setCreating(false);
                },
                onError: (e) => toast(errText(e), "error"),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

function NewTriggerDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState("");
  const ok = /^[a-z_][a-z0-9_]*$/.test(name);
  return (
    <Modal
      title="New trigger"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!ok} onClick={() => onCreate(name)}>
            Create
          </button>
        </>
      }
    >
      <label className="field">
        <span>Trigger name (snake_case; also the file name)</span>
        <input className="inp" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="descent_10k" />
      </label>
      <p className="dim small" style={{ margin: 0 }}>
        A starter trigger is generated; edit the condition, the snapshot payload and bind it to a report from the Reports screen.
      </p>
    </Modal>
  );
}
